import type { AppPlatformType } from "@lib/dashboardConfig";
import type { LabelGetter } from "@lib/localizedLabelsProvider/localizedLabelsProvider";
import type { NestedSchemaTypeLabelsOptions } from "@lib/localizedLabelsProvider/nestedSchemaTypeLabelsGetter";
import type { SHARED_CONSTANTS_PLATFORM_APP_TYPES, SHARED_CONSTANTS_PUSH_TOKEN_TYPES } from "@lib/shared_constants";

import type { subscriptionState } from "../../../typedSubscriptionConstants";
import type { SubscriptionStatuses } from "./constants";

export enum ModalVariant {
  Default,
  ModifyDataTypes,
  ViewValues,
}

export interface BaseSchemaProps<Options extends string = NestedSchemaTypeLabelsOptions> {
  closeModal: () => void;
  filterOnSearch: boolean;
  onNestedPropertyNameChange: (s: string) => void;
  search: string;
  variant: ModalVariant;
  modifySchema?: (path: string, type: Options, currentObject?: any, newSchema?: any) => void;
  nestedSchemaTypeLabel: LabelGetter<Options>;
}

export interface NestedSchemaItemButtonProps extends Omit<BaseSchemaProps, "search" | "filterOnSearch"> {
  item: NestedItem;
}

export interface NestedSchemaItemProps extends BaseSchemaProps {
  item: NestedItem;
}

export interface NestedSchemaBranchValueProps
  extends Omit<
    BaseSchemaProps,
    "closeModal" | "onNestedPropertyNameChange" | "modifySchema" | "search" | "filterOnSearch"
  > {
  isOpen: boolean;
  forcedOpen: boolean;
  schema: any;
}

export interface CollapsibleSchemaProps
  extends BaseSchemaProps,
    Pick<NestedSchemaBranchValueProps, "forcedOpen" | "schema"> {
  forceClose: () => void;
}

export interface NestedSchemaSelectorProps extends Omit<BaseSchemaProps, "nestedSchemaTypeLabel"> {
  forceClose: () => void;
  forcedOpen: boolean;
  schema: NestedItem[];
}

export interface NestedSchemaHighlightsProps {
  haystack: string;
  needle: string;
}

export interface NestedSchemaSelectorCollapsibleProps {
  customAttribute: string;
  onModalClose: () => void;
  onForceClose: () => void;
  propsObservable: any;
  search: string;
  schema: any;
  setCurrentSchema: (schema: any) => void;
  treeIsForcedOpen: boolean;
  variant: ModalVariant;
}

export interface NestedSchemaSelectorModalBodyProps
  extends Pick<NestedSchemaSelectorCollapsibleProps, "customAttribute" | "propsObservable" | "search" | "schema"> {
  onSearch: (search: string) => void;
  setOpen: (isOpen: boolean) => void;
  treeIsOpen: boolean;
  setTreeIsOpen: (isOpen: boolean) => void;
  canRegenerateSchema: boolean;
  updateIsGeneratingSchema: (state: boolean) => void;
  variant: ModalVariant;
}

// Nested item types

interface BaseNestedItemLeafNode {
  displayName: string;
  children?: never;
}

interface NestedItemDefaultLeafNode extends BaseNestedItemLeafNode {
  path: string;
  type: NestedSchemaTypeLabelsOptions;
  value?: never;
}

interface timeOperator {
  $time: string;
}

interface NestedItemViewValuesLeafNode extends BaseNestedItemLeafNode {
  path?: never;
  type?: NestedSchemaTypeLabelsOptions;
  attribute_type?: NestedSchemaTypeLabelsOptions;
  value: string | boolean | number | timeOperator;
}

interface NestedItemModifyDataTypesLeafNode extends BaseNestedItemLeafNode {
  path: string;
  type: NestedSchemaTypeLabelsOptions;
  value?: never;
}

type NestedItemLeafNode =
  | NestedItemDefaultLeafNode
  | NestedItemViewValuesLeafNode
  | NestedItemModifyDataTypesLeafNode;

interface NestedItemBranchNode {
  displayName: string;
  children: NestedItem[];
  path?: never;
  type?: never;
  attribute_type?: never;
  value?: never;
}

export type NestedItem = NestedItemBranchNode | NestedItemLeafNode;

export interface GetUserInfoAnyUsersParams {
  app_group_id: string;
}

export interface UserInfoAnyUsersResponse {
  has_any_users: boolean;
}

export interface GetUserInfoByIdentifierParams extends Record<string, unknown> {
  user_identifier: string;
  app_group_id: string;
  search_test_user_names: boolean;
  check_orphaned_users: boolean;
}

export interface GetUserInfosByIdentifierParams extends GetUserInfoByIdentifierParams {
  page_number: number;
}

export interface CustomEvent {
  /** name of the event */
  name: string;
  /** the number of times the event happened */
  count: number;
  /** the last time this type of event happened */
  last_did: number;
}

export interface BaseCustomAttr {
  name: string;
  value?: string;
  data?: {
    value: any;
    type: string;
    attribute_type?: string;
  };
}

export interface NestedCustomAttr extends BaseCustomAttr {
  array_view: NestedItem[];
}

export interface FlatCustomAttr extends BaseCustomAttr {
  array_view?: never;
}

export type CustomAttribute = FlatCustomAttr | NestedCustomAttr;

export interface RecentInfoByPlatform {
  model: string;
  os: string;
  last_app_version_used: string;
}

export interface SessionByApp {
  name: string;
  type: string;
  fs: number;
  ls: number;
}

export type AppType = SHARED_CONSTANTS_PLATFORM_APP_TYPES[keyof SHARED_CONSTANTS_PLATFORM_APP_TYPES];

export type PushTokenType = SHARED_CONSTANTS_PUSH_TOKEN_TYPES[keyof SHARED_CONSTANTS_PUSH_TOKEN_TYPES];

export interface PushApp {
  /** name of the app */
  app_name: string;
  /** type of the app, e.g. Android, iOS, Kindle, Web, etc. */
  app_type: AppType;
  /** type of the push token: background, foreground, ios live activity or provisional */
  token_type: PushTokenType;
  /** token string value */
  token_string: string;
  /** live activity attributes type, only apply for live activity tokens */
  attributes_type?: string;
}

export interface PushError {
  app: string;
  time: number;
  moved: boolean;
  message: string;
  token: string;
}

export interface UserInfoWorkflowPathObj {
  variant_name: string;
  last_message_received: number;
  last_step_name: string;
}

export interface UserInfoWorkflowObj {
  ag_id: string;
  id: string;
  last_received: number;
  last_step_received?: string;
  variants_entered?: string[];
  name: string;
  paths?: UserInfoWorkflowPathObj[];
}

export type GenderType = "male" | "female" | "other" | "unknown" | "not applicable" | "prefer not to say";

export type PredictionTypeToSummaries = {
  [platform in AppPlatformType]?: {
    model: string;
    os: string;
    last_app_version_code?: string;
    last_app_version_used: string;
    browser?: string;
    browser_version?: string;
  };
};

export interface TestUser {
  id: string;
  app_group_id: string;
  test_name: string;
  external_id: string;
  email: string;
  internal_user_groups_membership_ids: string[];
}

export interface UserInfoResponse {
  id: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  gender: GenderType;
  external_id: string;
  campaign_sampling_bucket: number;
  // TODO: replace any with actual type
  age_group: any | null;
  // TODO: replace any with actual type
  dob: any | null;
  // TODO: replace any with actual type
  test_user: boolean;
  test_user_data?: TestUser;
  image_url: string;
  city: string;
  country: string;
  language_code: string;
  language: string;
  locale: string;
  invalid_phone_number: boolean;
  // TODO: replace any with actual type
  phone_number: any | null;
  // TODO: replace any with actual type
  e164_phone_number: any | null;
  can_view_phone: boolean;
  last_app_version_used: string;
  session_count: number;
  median_session_duration: number;
  in_app_purchase_total: number;
  push_subscription_state: subscriptionState;
  push_subscription_time: number;
  email_subscription_state: subscriptionState;
  email_subscription_time: number;
  email_open_tracking_enabled: boolean;
  email_click_tracking_enabled: boolean;
  // TODO: replace any with actual type
  email_subscription_groups: any[];
  // TODO: replace any with actual type
  sms_subscription_groups: any[];
  whats_app_subscription_groups: any[];
  whats_app_bsuid: string | null;
  line_subscription_groups: any[];
  line_id: string | null;
  can_view_line_id?: boolean;
  marked_as_spam: boolean;
  hard_bounced: boolean;
  soft_bounce_reason: string | null;
  timezone: string;
  email: string;
  can_view_email: boolean;
  device_count: number;
  // TODO: replace any with actual type
  user_aliases: any[];
  most_recent_location: [number, number];
  purchases: CustomEvent[];
  total_number_of_purchases: number;
  custom_events: CustomEvent[];
  custom_attributes: CustomAttribute[];
  custom_attributes_strings: CustomAttribute[];
  custom_attributes_with_types?: CustomAttribute[];
  last_used: number;
  first_used: number;
  // TODO: replace any with actual type
  uninstalled_at: any | null;
  last_received_any_campaign: number;
  last_received_email_campaign: number;
  last_received_kakao_campaign: number;
  last_received_line_campaign: number;
  last_received_webhook_campaign: number;
  last_received_whats_app_campaign: number;
  last_received_push_campaign: number;
  last_made_purchase: number;
  last_received_sms_campaign: number;
  recent_info_by_platform: PredictionTypeToSummaries | null;
  has_push: boolean;
  // TODO: replace any with actual type
  most_recent_watch: any | null;
  sessions_by_app: SessionByApp[];
  push_apps: PushApp[];
  push_errors: PushError[];
  campaigns_received: any[];
  workflows_received: UserInfoWorkflowObj[] | {};
  // TODO: replace any with actual type
  attributed_campaign: any | null;
  // TODO: replace any with actual type
  attributed_source: any | null;
  // TODO: replace any with actual type
  attributed_adgroup: any | null;
  // TODO: replace any with actual type
  attributed_ad: any | null;
  // TODO: replace any with actual type
  attributed_at: any | null;
  // TODO: replace any with actual type
  prediction_type_to_summaries: any | null;
  is_global_control_member: boolean;
  user_ids_found: string[];
  user_ids_to_external_ids_found: { [key: string]: string | null };
  user_ids_to_pending_deletion_found?: { [key: string]: boolean };
  user_ids_to_test_user_found?: { [key: string]: boolean };
  user_ids_to_in_global_control_group_found?: { [key: string]: boolean };
  gaids?: string;
  idfas?: string;
  idfvs?: string;
  raids?: string;
  waids?: string;
  total_revenue?: number;
  total_refunds?: number;
  total_orders?: number;
  devices?: DeviceInfo[];
  deletion_records?: string[];
  shopping_carts?: Record<string, ShoppingCart>;
}

export interface DeviceInfo {
  id: string;
  platform: string;
  model: string | null;
  browser: string | null;
  browser_version: string | null;
}

export interface CartProduct {
  product_id: string;
  product_name: string;
  variant_id?: string;
  image_url?: string;
  product_url?: string;
  quantity: number;
  price: number;
  metadata?: Record<string, unknown>;
}

export interface ShoppingCart {
  cart_id: string;
  products: CartProduct[];
  total_value: number | null;
  updated_at: number | null;
  checkout_status?: string;
}

export type UserInfo = Omit<UserInfoResponse, "user_ids_found" | "user_ids_to_external_ids_found">;

export interface UserInfosResponse {
  /** The number of results returned in data */
  hits: number;
  /** Paginated information about users related to the search */
  data: Array<UserInfo>;
  /** The total number of results regardless of pagination */
  total_results: number;
}

export interface SelectedCampaign {
  ag_id: string;
  id: string;
  name: string;
  /** timestamp in seconds */
  when_received: number;
}

export interface UserInfoCampaignsResponse {
  campaigns_received: SelectedCampaign[];
}

export interface UserInfoSegmentsOptions {
  skip?: number;
  limit?: number;
  segment_ids?: string[];
}

export interface SegmentData {
  /** app group id */
  ag_id: string;
  /** Id of the segment */
  id: string;
  /** name of the segment */
  name: string;
}

export interface UserInfoSegmentsResponse {
  segments_data: SegmentData[];
}

export interface LinkedBusinessAccountRow {
  account_id: string;
  account_name: string;
  role: string | null;
}

export interface UserInfoLinkedBusinessAccountsResponse {
  linked_business_accounts: LinkedBusinessAccountRow[];
  has_more?: boolean;
  total_count?: number;
}

export interface UserInfoLinkedBusinessAccountsApiProps {
  userIdentifier: string;
  pageNumber: number;
  pageSize: number;
}

export interface UnlinkedBusinessAccountRow {
  account_id: string;
  account_name: string;
}

export interface UserInfoUnlinkedBusinessAccountsResponse {
  unlinked_business_accounts: UnlinkedBusinessAccountRow[];
  hits: number;
}

export interface UserInfoUnlinkedBusinessAccountsApiProps {
  userIdentifier: string;
  searchQuery: string;
  pageNumber: number;
  pageSize: number;
}

export interface BusinessAccountLinkPayload {
  account_id: string;
  role: string;
}

export interface PostUserInfoLinkBusinessAccountsByIdentifierProps {
  userIdentifier: string;
  accounts: BusinessAccountLinkPayload[];
}

export interface UserInfoLinkBusinessAccountsResponse {
  linked_business_accounts: LinkedBusinessAccountRow[];
}

export interface UserInfoLinkBusinessAccountsErrorResponse {
  error?: string;
  message?: string;
}

export interface PatchUserInfoLinkedBusinessAccountRoleParams {
  userId: string;
  account_id: string;
  role: string;
}

export interface DeleteUserInfoLinkedBusinessAccountParams {
  userId: string;
  accountId: string;
}

/**
 * As defined by app/controllers/controller_actions/apps/user_management.rb `find_push_token_location`
 */
export interface PushTokenStatusPayload {
  removed: boolean;
}

export type SubscriptionStatus = (typeof SubscriptionStatuses)[keyof typeof SubscriptionStatuses];

export interface PutUserInfoUpdateSubscriptionStatusParams {
  userId: string;
  app_group_id: string;
  status: SubscriptionStatus;
  message_type: "email" | "push";
}

export interface PutUserInfoUpdateSubscriptionStatusResponse {
  info: string;
  push_subscription_state: SubscriptionStatus;
  push_subscription_time: number;
  email_subscription_state: SubscriptionStatus;
  email_subscription_time: number;
}

export enum SubscriptionGroupStatus {
  UNSUBSCRIBED = 1,
  SUBSCRIBED = 0,
}

export interface PutUserInfoUpdateSubscriptionGroupStatusParams {
  userId: string;
  group_bson_id: string;
  status: SubscriptionGroupStatus;
  message_type: "email" | "sms" | "whatsApp";
  locale?: string;
}

export interface PutUserInfoUpdateSubscriptionGroupStatusResponse {
  info: string;
}
